import type { DashboardInput, DashboardPeriod } from '@sirius/contracts/admin'
import { DASHBOARD_PERIODS, idSchema } from '@sirius/contracts/admin'

/** The time zone of every dashboard bucket and date (the backend's too). */
export const DASHBOARD_TIME_ZONE = 'Europe/Berlin'

export const DEFAULT_PERIOD: DashboardPeriod = '7d'

/**
 * The dashboard's filters as the URL holds them (`?period=&until=&location=
 * &cabinet=`). `undefined` is the default: now for `until`, every location or
 * cabinet. The tenant is always the host's, as on every page.
 */
export type DashboardFilters = {
  period: DashboardPeriod
  /** a Berlin calendar day, `YYYY-MM-DD`: the window ends with it */
  until: string | undefined
  location: string | undefined
  cabinet: string | undefined
}

const isId = (value: string | null): value is string =>
  value !== null && idSchema.safeParse(value).success

const isPeriod = (value: string | null): value is DashboardPeriod =>
  (DASHBOARD_PERIODS as readonly (string | null)[]).includes(value)

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * The earliest year a dashboard date may name: a date input reports a year
 * being typed digit by digit (`0002-01-14`), which must not count as a date.
 */
export const MIN_YEAR = 1970

function dateParts(value: string) {
  const match = DATE_PATTERN.exec(value)
  if (match === null) {
    return undefined
  }
  const [year, month, day] = [match[1], match[2], match[3]].map(Number) as [
    number,
    number,
    number,
  ]
  if (year < MIN_YEAR) {
    return undefined
  }
  // setUTCFullYear, not Date.UTC: Date.UTC maps the years 0-99 to 1900-1999
  const date = new Date(0)
  date.setUTCFullYear(year, month - 1, day)
  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? { year, month, day }
    : undefined
}

/** Whether `value` is a calendar day written `YYYY-MM-DD`, from `MIN_YEAR` on. */
export function isBerlinDate(value: string) {
  return dateParts(value) !== undefined
}

const berlinParts = new Intl.DateTimeFormat('en-US', {
  timeZone: DASHBOARD_TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
})

/** How far Berlin's wall clock is ahead of UTC at `instant`, in ms. */
function berlinOffset(instant: number) {
  const parts = Object.fromEntries(
    berlinParts
      .formatToParts(new Date(instant))
      .map(({ type, value }) => [type, Number(value)]),
  ) as Record<'year' | 'month' | 'day' | 'hour' | 'minute' | 'second', number>
  const wallClock = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  )
  return wallClock - Math.floor(instant / 1000) * 1000
}

/**
 * The end of the Berlin day `until` (`YYYY-MM-DD`): the start of the next
 * Berlin day, so a window `until` it covers the whole day.
 */
export function untilToDate(until: string): Date {
  const parts = dateParts(until)
  if (parts === undefined) {
    throw new Error(`not a date: ${until}`)
  }
  // the next day's midnight on a UTC clock, then moved by Berlin's offset
  // (midnight is never skipped or repeated in Berlin: the clocks change at
  // 02:00 / 03:00)
  const wallClock = Date.UTC(parts.year, parts.month - 1, parts.day + 1)
  const guess = wallClock - berlinOffset(wallClock)
  return new Date(wallClock - berlinOffset(guess))
}

/** The filters in `params`; an invalid value falls back to its default. */
export function parseDashboardFilters(
  params: URLSearchParams,
): DashboardFilters {
  const period = params.get('period')
  const until = params.get('until')
  const location = params.get('location')
  const cabinet = params.get('cabinet')
  return {
    period: isPeriod(period) ? period : DEFAULT_PERIOD,
    until: until !== null && isBerlinDate(until) ? until : undefined,
    location: isId(location) ? location : undefined,
    cabinet: isId(cabinet) ? cabinet : undefined,
  }
}

/** The URL search params of `filters`, defaults left out. */
export function serializeDashboardFilters(
  filters: DashboardFilters,
): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.period !== DEFAULT_PERIOD) {
    params.set('period', filters.period)
  }
  const optional = [
    ['until', filters.until],
    ['location', filters.location],
    ['cabinet', filters.cabinet],
  ] as const
  for (const [name, value] of optional) {
    if (value !== undefined) {
      params.set(name, value)
    }
  }
  return params
}

/**
 * The input every card sends (`dashboard.*`), without undefined keys: always
 * the host's tenant, staff included.
 */
export function dashboardInput(
  filters: DashboardFilters,
  hostTenantId: string,
): DashboardInput {
  return {
    period: filters.period,
    ...(filters.until === undefined
      ? {}
      : { until: untilToDate(filters.until) }),
    tenantId: hostTenantId,
    ...(filters.location === undefined ? {} : { locationId: filters.location }),
    ...(filters.cabinet === undefined ? {} : { cabinetId: filters.cabinet }),
  }
}

export const withPeriod = (
  filters: DashboardFilters,
  period: DashboardPeriod,
): DashboardFilters => ({ ...filters, period })

/** A day that is not `YYYY-MM-DD` (a half-typed date) means now. */
export const withUntil = (
  filters: DashboardFilters,
  until: string | undefined,
): DashboardFilters => ({
  ...filters,
  until: until !== undefined && isBerlinDate(until) ? until : undefined,
})

/**
 * Another location (undefined: every location). The cabinet stays only when
 * it stands there (`cabinetLocationId`: its location, null in storage,
 * undefined when unknown).
 */
export const withLocation = (
  filters: DashboardFilters,
  location: string | undefined,
  cabinetLocationId: string | null | undefined,
): DashboardFilters => ({
  ...filters,
  location,
  cabinet:
    location === undefined || cabinetLocationId === location
      ? filters.cabinet
      : undefined,
})

export const withCabinet = (
  filters: DashboardFilters,
  cabinet: string | undefined,
): DashboardFilters => ({ ...filters, cabinet })
